import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

// Local design-system Radio, modeled directly on Checkbox.tsx's real
// structure (same props shape, same label-wraps-everything touch-target
// pattern, same focus-visible/disabled/error mechanics) rather than
// inventing a different shape for a sibling input type. Differences from
// Checkbox: native type="radio" (grouping is via the standard `name`
// attribute, passed straight through via ...rest — no group-state manager
// here, nothing in this app has asked for one yet), a circular indicator
// with an inner dot instead of a checkmark/indeterminate bar, and no
// indeterminate concept (radios don't have one).
export type RadioSize = "sm" | "md";

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size" | "onChange"> {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: RadioSize;
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, description, error, size = "md", disabled, className, id: providedId, onChange, ...rest },
  ref
) {
  const fallbackId = useId();
  const inputId = providedId ?? fallbackId;
  const descId = description ? `${inputId}-desc` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;
  const describedBy = [descId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <label
      htmlFor={inputId}
      className={cn("rc-radio", `rc-radio--${size}`, disabled && "rc-radio--disabled", error && "rc-radio--error", className)}
    >
      <input
        ref={ref}
        id={inputId}
        type="radio"
        className="rc-radio__input"
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange?.(e.target.checked, e)}
        {...rest}
      />
      <span className="rc-radio__box" aria-hidden="true">
        <span className="rc-radio__dot" />
      </span>
      <span className="rc-radio__text">
        <span className="rc-radio__label">{label}</span>
        {description ? (
          <span id={descId} className="rc-radio__description">
            {description}
          </span>
        ) : null}
        {error ? (
          <span id={errorId} className="rc-radio__error" role="alert">
            {error}
          </span>
        ) : null}
      </span>
    </label>
  );
});
